/** TYPO : valeurs mesurées, sans arrondi des graisses ni CSS arbitraire. */
export const TYPOGRAPHY_ROLES = ['pageTitle', 'eyebrow', 'cardTitle', 'heroValue', 'value', 'smallValue', 'label', 'unit', 'caption', 'subValue', 'body'] as const;
export type TypographyRole = typeof TYPOGRAPHY_ROLES[number];
export interface TypographyStyle {
  fontSize: number;
  fontWeight: number;
  letterSpacing: number;
  textTransform: 'none' | 'uppercase' | 'lowercase' | 'capitalize';
  /** Hauteur en pixels CSS, ou normal. */
  lineHeight: number | 'normal';
  fontVariantNumeric: 'normal' | 'tabular-nums' | 'lining-nums' | 'tabular-nums lining-nums' | 'lining-nums tabular-nums';
}
export interface MeasuredTypography {
  stack?: string[];
  roles?: Partial<Record<TypographyRole, TypographyStyle>>;
}
const bounded = (value: unknown, min: number, max: number): value is number => typeof value === 'number' && Number.isFinite(value) && value >= min && value <= max;
export const safeFontStack = (value: unknown): string[] | undefined => {
  if (!Array.isArray(value) || value.length === 0 || value.length > 12 || !value.every(name => typeof name === 'string' && /^[A-Za-z][A-Za-z0-9 -]{0,59}$|^-[A-Za-z][A-Za-z0-9-]{0,58}$/.test(name))) return undefined;
  return [...value];
};
export const safeTypographyStyle = (value: unknown): TypographyStyle | undefined => {
  if (!value || typeof value !== 'object') return undefined;
  const s = value as TypographyStyle;
  if (!bounded(s.fontSize, 8, 72) || !bounded(s.fontWeight, 100, 900) || !bounded(s.letterSpacing, -3, 4) ||
    !['none', 'uppercase', 'lowercase', 'capitalize'].includes(s.textTransform) ||
    !(s.lineHeight === 'normal' || bounded(s.lineHeight, 8, 144)) ||
    !['normal', 'tabular-nums', 'lining-nums', 'tabular-nums lining-nums', 'lining-nums tabular-nums'].includes(s.fontVariantNumeric)) return undefined;
  return { fontSize: s.fontSize, fontWeight: s.fontWeight, letterSpacing: s.letterSpacing, textTransform: s.textTransform, lineHeight: s.lineHeight, fontVariantNumeric: s.fontVariantNumeric };
};
export const typographyVariables = (typography: MeasuredTypography): Record<string, string> => {
  const variables: Record<string, string> = {};
  const stack = safeFontStack(typography.stack);
  if (stack) {
    const family = stack.map(name => name.includes(' ') ? `"${name}"` : name).join(', ');
    variables['--font-body'] = family;
    variables['--font-display'] = family;
    variables['--font-label'] = family;
  }
  for (const role of TYPOGRAPHY_ROLES) {
    const s = safeTypographyStyle(typography.roles?.[role]);
    if (!s) continue;
    const prefix = `--module-type-${role}`;
    // rem respecte le zoom et la préférence de taille du navigateur ; plancher 10 px à 16 px racine.
    variables[`${prefix}-size`] = `max(0.625rem, ${s.fontSize / 16}rem)`;
    variables[`${prefix}-weight`] = String(s.fontWeight);
    variables[`${prefix}-spacing`] = `${s.letterSpacing / 16}rem`;
    variables[`${prefix}-transform`] = s.textTransform;
    variables[`${prefix}-height`] = s.lineHeight === 'normal' ? 'normal' : String(s.lineHeight / s.fontSize);
    variables[`${prefix}-numeric`] = s.fontVariantNumeric;
  }
  return variables;
};
